import { Vector3 } from "three";
import type { Bbox2 } from "./bbox";
import { bboxCenterX, bboxCenterY } from "./bbox";

/**
 * Projected coordinates in real datasets are large (LV95 eastings are ~2.66e6 m), and
 * WebGL positions are `float32` with ~7 significant digits. Feeding raw coordinates to the
 * GPU therefore quantises geometry to ~0.25 m and makes the depth buffer unusable.
 *
 * Every layer is placed relative to a single scene origin chosen once from the first
 * dataset loaded. Positions handed to three.js are always `double - origin` computed in
 * JS doubles first, so the float32 values that reach the GPU stay small.
 *
 * Axis convention: the projected CRS is a right-handed East/North/Up frame, three.js is
 * Y-up, so the mapping is `x = E - originE`, `y = H - originH`, `z = -(N - originN)`.
 */
export class SceneOrigin {
  private e = 0;
  private n = 0;
  private h = 0;
  private fixed = false;

  get east(): number {
    return this.e;
  }
  get north(): number {
    return this.n;
  }
  get height(): number {
    return this.h;
  }
  get isSet(): boolean {
    return this.fixed;
  }

  /** Sets the origin once; later calls are ignored so all layers share one frame. */
  setOnce(east: number, north: number, height: number): boolean {
    if (this.fixed) return false;
    // Round to whole metres: keeps reported scene coordinates readable and makes the
    // origin reproducible when the same dataset is reloaded in a different order.
    this.e = Math.round(east);
    this.n = Math.round(north);
    this.h = Math.round(height);
    this.fixed = true;
    return true;
  }

  setOnceFromBbox(bbox: Bbox2, height = 0): boolean {
    return this.setOnce(bboxCenterX(bbox), bboxCenterY(bbox), height);
  }

  /** Projected (E, N, H) -> scene position. */
  toScene(east: number, north: number, height: number, out = new Vector3()): Vector3 {
    return out.set(east - this.e, height - this.h, -(north - this.n));
  }

  sceneX(east: number): number {
    return east - this.e;
  }
  sceneY(height: number): number {
    return height - this.h;
  }
  sceneZ(north: number): number {
    return -(north - this.n);
  }

  /** Scene position -> projected (E, N, H). */
  toProjected(p: { x: number; y: number; z: number }): { east: number; north: number; height: number } {
    return { east: p.x + this.e, north: this.n - p.z, height: p.y + this.h };
  }
}
